import React from "react";
import { Outlet, useLocation, useOutletContext } from "react-router-dom";

import { useTranslation } from "react-i18next";

import AccessBlocked from "../../routes/AccessBlocked";

function LoadingBlock() {
  return <div style={{ padding: 24 }}>Loading...</div>;
}

/* =========================================================
   DOSSIER DRAFT GUARD
   ========================================================= */

export function HomebuyerDossierDraftGuard() {
  const outletContext = useOutletContext();

  const { dashboardSummary, isLoadingMe } = outletContext || {};

  const { t } = useTranslation("guard");

  if (isLoadingMe) {
    return <LoadingBlock />;
  }

  const status = dashboardSummary?.dossier_status;

  const canAccessDossierSteps =
    !dashboardSummary?.has_submitted_dossier || status === "DRAFT";

  if (!canAccessDossierSteps) {
    return (
      <AccessBlocked
        title={t("guards.dossierLockedTitle")}
        description={t("guards.dossierLockedDescription")}
        primaryActionLabel={t("common.goToDashboard")}
        primaryActionPath="/hb/dashboard"
        secondaryActionLabel={t("common.goBack")}
      />
    );
  }

  return <Outlet context={outletContext} />;
}

/* =========================================================
   OSS CONNECTION GUARD
   ========================================================= */

export function HomebuyerOssConnectionGuard() {
  const outletContext = useOutletContext();

  const { dashboardSummary, isLoadingMe } = outletContext || {};

  const { t } = useTranslation("guard");

  const location = useLocation();

  if (isLoadingMe) {
    return <LoadingBlock />;
  }

  const ossStatus = String(
    dashboardSummary?.oss_connection_status || "",
  ).toUpperCase();

  const ossName =
    dashboardSummary?.oss_name ||
    t("guards.selectedOss", "your selected One-Stop Shop");

  const isChangeOssMode = location.state?.changeOssMode === true;

  if (isChangeOssMode) {
    return <Outlet context={outletContext} />;
  }

  /* =======================================================
     PENDING OSS CONNECTION
     ======================================================= */

  if (dashboardSummary?.has_oss_connection && ossStatus === "PENDING") {
    return (
      <AccessBlocked
        title={t(
          "guards.ossPendingLockedTitle",
          "One-Stop Shop request under review",
        )}
        description={t("guards.ossPendingLockedDescription", {
          defaultValue:
            "Your connection request has already been sent to {{ossName}} and is currently awaiting their response.",
          ossName,
        })}
        primaryActionLabel={t("common.goToDashboard", "Go to dashboard")}
        primaryActionPath="/hb/dashboard"
        secondaryActionLabel={t("common.goBack", "Go back")}
      />
    );
  }

  /* =======================================================
     ACCEPTED OSS CONNECTION
     ======================================================= */

  if (dashboardSummary?.has_oss_connection && ossStatus === "ACCEPTED") {
    return (
      <AccessBlocked
        title={t(
          "guards.ossAcceptedLockedTitle",
          "One-Stop Shop already connected",
        )}
        description={t("guards.ossAcceptedLockedDescription", {
          defaultValue:
            "You are already connected with {{ossName}}. Your One-Stop Shop connection is active and your renovation collaboration can continue from the dashboard.",
          ossName,
        })}
        primaryActionLabel={t("common.goToDashboard", "Go to dashboard")}
        primaryActionPath="/hb/dashboard"
        secondaryActionLabel={t("common.goBack", "Go back")}
      />
    );
  }

  return <Outlet context={outletContext} />;
}
